import { HttpError } from './http-error'
import type { AdvanceSessionResponse } from './types'

/**
 * The one route a session token is ever presented to (build plan Track F, F1/F2). The scan client
 * beside this one is unaware anything above one assessment exists - this is deliberately a separate
 * client, on a separate credential, rather than a widened `ScanClient` (docs/decisions.md D-57).
 */
export interface SessionClient {
  /**
   * Asks the server "given the session token I already hold, what should I be looking at" - the
   * one call `resolving-step` makes, on first entry and on every mid-session reload alike.
   */
  advance(sessionToken: string): Promise<AdvanceSessionResponse>
}

/** `GET`/`POST /v1/public/session/advance` answers 404 for an unknown or expired session - this app treats that identically to every other "nothing left here" outcome. */
export class SessionUnavailableError extends Error {
  constructor() {
    super('This session is no longer available.')
    this.name = 'SessionUnavailableError'
  }
}

export function createSessionClient(baseUrl: string): SessionClient {
  return {
    async advance(sessionToken) {
      const response = await fetch(`${baseUrl}/v1/public/session/advance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
      })
      if (response.status === 404) {
        throw new SessionUnavailableError()
      }
      if (!response.ok) {
        throw new HttpError(response.status, `advance failed with ${response.status}`)
      }
      return (await response.json()) as AdvanceSessionResponse
    },
  }
}
